import { PageTitle } from '../../../components/texts';
import { TyTable, TyTableCell, TyTableRow } from '@tymbe/ty-components/table';
import { TableHead, TablePagination, useTablePagination } from '../../../components/Table';
import { TableHeadCellSortable } from '../../../components/Table/TableHeadCellSortable';
import useTableSort from '../../../components/Table/useTableSort';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import feathersClient from '../../../apiClient';
import { getPaginatedResponse } from '../../../apiClient/utils';
import Spinner from '../../../components/Spinner';
import moment from 'moment';
import { VodafoneCsvState } from '@tymbe/schema/enums';
import { FavouriteIcon, FileIcon } from '@tymbe/ui-icons';
import { useDocumentDownload } from '../../../hooks/useDocumentDownload';
import { PrimaryButton, SecondaryButton } from '@tymbe/ui-primitives/*';
import { ModalShell } from '@tymbe/ty-components/modals';
import { useState } from 'react';
import { ErrorAlert, SuccessAlert } from '../../../components/alerts';
import { Conflict } from '@feathersjs/errors';

const getStateLabel = (state: VodafoneCsvState | string) => {
  switch (state) {
    case VodafoneCsvState.PENDING:
      return 'Čeká na zpracování';
    case VodafoneCsvState.PROCESSED:
      return 'Zpracováno';
    case VodafoneCsvState.FAILED:
      return 'Chyba při zpracování';
    default:
      return 'Neznámý stav';
  }
};

const VodafoneImpotsPage = () => {
  const { addSort, sortString: sort } = useTableSort({ initialSortField: 'id', initialSort: -1 });
  const [field, sortOrder] = sort?.split('=') ?? ['id', -1];
  const { setPageParams, pageSize, pageStart } = useTablePagination();
  const [modalOpen, setModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File>();

  const { data, isLoading } = useQuery({
    queryKey: [
      'vodafone-imports',
      { $sort: Number(sortOrder) ? { [field]: Number(sortOrder) } : undefined, $limit: pageSize, $skip: pageStart }
    ] as const,
    queryFn: async ({ queryKey: [, q] }) => {
      const result = await feathersClient
        .service('vodafone-csv')
        .find({
          query: {
            $eager: '[createdBy]',
            ...q
          }
        });
      return getPaginatedResponse(result);
    }
  });

  const { downloadDocument } = useDocumentDownload();

  const { mutateAsync: uploadFile } = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('csvFile', file);
      await feathersClient.service('vodafone-csv').create(formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries('vodafone-imports');
    }
  });

  const handleFileUpload = async () => {
    if (!file) return;
    try {
      await uploadFile(file);
      setFile(undefined);
      setModalOpen(false);
      SuccessAlert('Soubor byl úspěšně nahrán');
    } catch (error) {
      if (error instanceof Conflict) {
        ErrorAlert('Tento soubor již byl nahrán.');
        return;
      }
      if (error instanceof Error) {
        ErrorAlert(`Chyba při nahrávání souboru: ${error.message}`);
        return;
      }
      ErrorAlert('Neznámá chyba při nahrávání souboru.');
    }
  };

  return (
    <>
      <div className="flex items-center">
        <PageTitle>
          Vodafone importy
        </PageTitle>
        <PrimaryButton className="ml-auto" onClick={() => setModalOpen(true)}>Nahrát CSV</PrimaryButton>
      </div>
      <div>
        <TyTable className="w-full">
          <TableHead>
            <TyTableRow>
              <TableHeadCellSortable {...addSort('id')}>ID</TableHeadCellSortable>
              <TableHeadCellSortable {...addSort('csv_url')}>CSV cesta</TableHeadCellSortable>
              <TableHeadCellSortable {...addSort('state')}>Stav</TableHeadCellSortable>
              <TableHeadCellSortable {...addSort('created_by')}>Vytvořeno</TableHeadCellSortable>
              <TableHeadCellSortable {...addSort('processed_at')}>Zpracováno</TableHeadCellSortable>
              <TableHeadCellSortable {...addSort('created_at')}>Vytvořeno</TableHeadCellSortable>
              <TableHeadCellSortable {...addSort('updated_at')}>Aktualizováno</TableHeadCellSortable>
            </TyTableRow>
          </TableHead>
          <tbody>
            {isLoading && <TyTableRow><TyTableCell colSpan={7} align="center"><Spinner
              show={isLoading}
              className="w-6"
            /></TyTableCell></TyTableRow>}
            {data?.data.length === 0 ? <TyTableRow><TyTableCell colSpan={7} align="center">Žádná
              data</TyTableCell></TyTableRow> : null}
            {data?.data.map((item) => (
              <TyTableRow key={item.id}>
                <TyTableCell className="text-center">{item.id}</TyTableCell>
                <TyTableCell>
                  {item.csv_url}
                  <FileIcon
                    name="download"
                    className="size-5 inline-block cursor-pointer border rounded align-middle hover:text-primary-500 ml-1"
                    onClick={() => downloadDocument(item.csv_url)}
                  />
                </TyTableCell>
                <TyTableCell>{getStateLabel(item.state)}</TyTableCell>
                <TyTableCell>{`${item.createdBy?.first_name} ${item.createdBy?.last_name}`}</TyTableCell>
                <TyTableCell>{item.processed_at ? moment(item.processed_at).format('L') : 'N/A'}</TyTableCell>
                <TyTableCell>{moment(item.created_at).format('L')}</TyTableCell>
                <TyTableCell>{moment(item.updated_at).format('L')}</TyTableCell>
              </TyTableRow>
            ))}
          </tbody>
        </TyTable>
        <TablePagination
          {...setPageParams()}
          rowsCount={data?.total}
        />
      </div>
      <ModalShell
        showModal={modalOpen}
        className="flex flex-col w-3/5 max-w-full h-3/5 p-4 gap-4"
        onClose={() => setModalOpen(false)}
      >
        <h2 className="text-lg font-semibold">Nahrát dokument</h2>
        <FavouriteIcon
          name="close"
          className="size-8 absolute right-4 top-4 cursor-pointer"
          onClick={() => setModalOpen(false)}
        />
        <label
          className="flex flex-col items-center justify-center flex-1 border border-dashed over p-4"
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();

            if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
            const file = e.dataTransfer.files[0];
            const [, ext] = file.name.split('.');

            if (ext !== 'csv') {
              ErrorAlert('Prosím, nahrajte pouze CSV soubory.');
              return;
            }

            setFile(file);
          }}
          htmlFor="vodafone-import-file-input"
          onDragOver={(e) => {
            e.preventDefault();
          }}
        >
          <div className="flex-1 flex items-center justify-center gap-2">
            {!file ? null : (file.name)}
          </div>
          <div className="flex-1">
            <SecondaryButton type="button" onClick={(e) => e.currentTarget.parentElement?.click()}>Vyberte
              soubor</SecondaryButton>
          </div>
          <input
            type="file"
            accept=".csv"
            className="hidden"
            id="vodafone-import-file-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setFile(file);
              }
            }}
          />
        </label>
        <PrimaryButton onClick={() => handleFileUpload()} disabled={!file}>Nahrát</PrimaryButton>
      </ModalShell>
    </>
  );
};

export default VodafoneImpotsPage;
